import { zodResolver } from "@hookform/resolvers/zod";
import { CustomInput } from "components/afterLogin/settings-menu-components/settingsSubComponents/CustomInput";
import { LoadingButton } from "components/afterLogin/settings-menu-components/settingsSubComponents/UI/LoadingButton";
import { CKEditor } from "@ckeditor/ckeditor5-react";
import ClassicEditor from "@ckeditor/ckeditor5-build-classic";
import moment from "moment";
import React, { useRef, useState } from "react";
import { Modal } from "react-bootstrap";
import { Controller, useForm } from "react-hook-form";
import { z } from "zod";
import _ from "lodash";
import { useHistory } from "react-router-dom";
import { postRequest } from "api/tools/httpRequests";
import { toast } from "react-toastify";
import { useAppSelector } from "flight-react/hooks/reduxHooks";
import { ENUMphoneNumberLabel } from "types/common.types";
import { IntlTelInputWrapper } from "components/afterLogin/dashboard/IntlTelInputWrapper";
import ReCaptcha from "../reCaptcha/ReCaptcha";

const tourPackageEnquirySchema = z.object({
  travelDate: z.string().nonempty("Travel Date is required"),
  title: z.string().min(2),
  adultCount: z.number().min(1, { message: "At least 1 adult is required" }),
  childCount: z.number().min(0, { message: "Child count cannot be negative" }),
  infantCount: z
    .number()
    .min(0, { message: "Infant count cannot be negative" }),
  fullName: z.string().min(1),
  email: z.string().email().min(1),
  phoneNumber: z.string().min(1),
  phoneCode: z.string().min(1),
  remark: z.string().optional(),
});

export type TTourPackageEnquiry = z.infer<typeof tourPackageEnquirySchema>;

const paxArray = [
  { key: "adultCount", label: "No of Adults" },
  { key: "childCount", label: "No of Children" },
  { key: "infantCount", label: "No of Infants" },
];

export const PackageEnquiryModal: React.FC<{
  onClose: () => void;
  show: boolean;
  packageName?: string;
}> = (props) => {
  const reCaptchaRef = useRef<any>();
  const userData = useAppSelector((state) => state.user);
  const [isValidPhoneNumber, setIsValidPhoneNumber] = useState(true);

  const [captchaData, setCaptchaData] = useState<string | undefined>(undefined);

  const primaryPhoneNumberObj = userData.phoneNumbers?.find(
    (phoneNumber) => phoneNumber.label === ENUMphoneNumberLabel.Primary
  );

  // const location = useLocation();
  // const authToken = getAuthToken();

  const history = useHistory();
  const [loading, setLoading] = useState(false);

  // useEffect(() => {
  //   if (!authToken) {
  //     let redirectTo = "";
  //     if (location.pathname) {
  //       redirectTo = `/?redirectTo=/app${encodeURIComponent(
  //         location.pathname
  //       )}&redirectType=external`;
  //     }
  //     history.push(`/login${redirectTo}`); // Redirect to login with the redirectTo query parameter
  //   }
  // }, [authToken, location.pathname, history]);
  const {
    register,
    handleSubmit,
    control,
    setValue,
    setError,
    clearErrors,
    watch,
    formState: { errors },
  } = useForm<TTourPackageEnquiry>({
    resolver: zodResolver(tourPackageEnquirySchema),
    defaultValues: {
      adultCount: 2,
      childCount: 0,
      infantCount: 0,
      travelDate: moment().add(6, "days").format("YYYY-MM-DD"),
      title: props.packageName,
      email: userData?.email ? userData.email : undefined,
      fullName: userData?.firstName
        ? `${userData.firstName} ${userData?.lastName}`
        : undefined,
      phoneNumber: primaryPhoneNumberObj?.phoneNumber
        ? primaryPhoneNumberObj.phoneNumber
        : undefined,
      phoneCode: primaryPhoneNumberObj?.primaryPhoneNumberObj
        ? primaryPhoneNumberObj.phoneCountryCode
        : undefined,
    },
  });

  const onSubmit = async (data) => {
    if (Object.keys(errors).length > 0 || !isValidPhoneNumber) {
      if (!isValidPhoneNumber) {
        setError("phoneNumber", {
          type: "manual",
          message: "Enter a valid phone number",
        });
      }
      toast.error("Please fix the errors before submitting.");
      return;
    }

    let token = captchaData;
    if (!token) {
      token = await reCaptchaRef.current.executeAsync();
      setCaptchaData(token);
    }

    if (!token) {
      return toast.error("Please fill captcha");
    }

    setLoading(true);
    const savedResponse = await postRequest(
      "packages/savePackageEnquiry",
      {
        packageData: data,
      },
      undefined,
      {
        "captcha-token": token,
      }
    );

    if (savedResponse.data.success) {
      history.push(
        `/package/enquiry/submitted/${
          savedResponse.data.result.enquiryId
        }?title=${encodeURIComponent(savedResponse.data.result.title)}`
      );
    } else {
      toast.error(
        savedResponse?.errorMessage ||
          "Something went wrong, please try again later."
      );
    }

    reCaptchaRef.current?.reset();
    setCaptchaData(undefined);

    setLoading(false);
    return;
  };

  return (
    <Modal
      show={props.show}
      onHide={props.onClose}
      backdrop="static"
      keyboard={false}
      centered
    >
      <Modal.Header closeButton>
        <Modal.Title>Get in touch with us !</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <form action="" onSubmit={handleSubmit(onSubmit)}>
          <CustomInput
            className="mb-2"
            label="Full Name"
            // {...register("title")}
            labelClassname="small"
            registerProps={{
              register,
              registerName: "fullName",
            }}
            required
            errorMessage={errors.fullName?.message}
          />
          <CustomInput
            className="mb-2"
            label="Email"
            // {...register("title")}
            labelClassname="small"
            registerProps={{
              register,
              registerName: "email",
            }}
            required
            errorMessage={errors.email?.message}
          />
          <Controller
            name="phoneNumber"
            control={control}
            render={({ field }) => (
              <IntlTelInputWrapper
                label="Phone Number"
                defaultValue={field.value} // Set default value for phone number
                dialCode={primaryPhoneNumberObj?.phoneCountryCode || "+91"} // Default dial code, if available
                onChange={(phoneNumber, phoneCode, isValid) => {
                  setIsValidPhoneNumber(isValid);
                  if (!isValid) {
                    setError("phoneNumber", {
                      type: "manual",
                      message: "Enter a valid phone number", // Custom error message
                    });
                  } else {
                    // Clear the error if the phone number is valid
                    clearErrors("phoneNumber");
                  }

                  // Update the phone number and phone code in the form when the user changes it
                  // field.onChange(phoneNumber); // Update phone number in the form
                  setValue("phoneNumber", phoneNumber);
                  setValue("phoneCode", phoneCode); // Update phone country code in the form
                }}
                isRequiredField={true}
                errorMessage={errors.phoneNumber?.message} // Show error message if exists
              />
            )}
          />
          <CustomInput
            className="mb-2"
            label="Title"
            // {...register("title")}
            labelClassname="small"
            registerProps={{
              register,
              registerName: "title",
            }}
            required
            errorMessage={errors.travelDate?.message}
          />

          <Controller
            name="travelDate"
            control={control}
            render={({ field }) => (
              <CustomInput
                label="Travel Date"
                labelClassname="small"
                type="date"
                id="travelDate"
                {...field}
                required
                errorMessage={errors.travelDate?.message}
              />
            )}
          />
          <div className="row justify-content-between">
            {paxArray.map((pax) => (
              <div key={pax.key} className="my-2 col-4">
                <label className="form-label small mb-0">{pax.label}</label>
                <select
                  className="form-select w-100"
                  {...register(pax.key as keyof TTourPackageEnquiry, {
                    valueAsNumber: true,
                  })}
                >
                  {_.range(0, 10 + 1).map((num) => (
                    <option key={num} value={num}>
                      {num}
                    </option>
                  ))}
                </select>
                {errors[pax.key] && (
                  <div className="text-danger">{errors[pax.key].message}</div>
                )}
              </div>
            ))}
          </div>
          <div>
            <Controller
              name="remark"
              control={control}
              defaultValue=""
              render={({ field }) => (
                <div>
                  <label className="form-label small mb-0">Remark</label>
                  <CKEditor
                    editor={ClassicEditor}
                    data={field.value}
                    onChange={(_, editor) => {
                      field.onChange(editor.getData());
                    }}
                  />
                  {errors.remark && (
                    <div className="text-danger">{errors.remark.message}</div>
                  )}
                </div>
              )}
            />
          </div>

          <div>
            {/* HCaptcha was here */}
            <ReCaptcha
              setToken={(token) => {
                setCaptchaData(token);
              }}
              myRef={reCaptchaRef}
            />
          </div>

          <div className="text-center my-3">
            <LoadingButton
              type="submit"
              className="px-4 py-2"
              loading={loading}
            >
              Submit Enquiry
            </LoadingButton>
          </div>
        </form>
      </Modal.Body>
      <Modal.Footer>
        {/* <Button variant="secondary" onClick={props.onClose}>
          Close
        </Button>
        <Button variant="primary">Understood</Button> */}
      </Modal.Footer>
    </Modal>
  );
};
